// Formatting helpers shared across screens. Centralized so currency/percent/
// date display is identical everywhere (Frontend Rules: no per-component
// formatting logic). Mirrors the prototype's fmtUSD.

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const USD_NO_CENTS = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** $284,612.40 (cents=true, default) or $284,612 (cents=false). */
export const formatCurrency = (n, cents = true) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return (cents ? USD : USD_NO_CENTS).format(Number(n));
};

/**
 * +$22,481 / −$1,234 — a signed money figure. The sign is a true minus (U+2212), not a
 * hyphen, so it lines up with the digits at tabular widths. `null` reads "—" (via
 * formatCurrency) rather than "+—": an absent figure has no direction.
 */
export const formatSignedCurrency = (n, cents = true) => {
  if (n == null || Number.isNaN(Number(n))) return formatCurrency(null);
  const v = Number(n);
  return `${v >= 0 ? '+' : '−'}${formatCurrency(Math.abs(v), cents)}`;
};

/** 8.4% — `digits` controls decimals; `signed` prefixes +/−. */
export const formatPercent = (n, { digits = 1, signed = false } = {}) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  const s = `${v.toFixed(digits)}%`;
  return signed && v > 0 ? `+${s}` : s;
};

/** A bare calendar date with no time part — "2026-06-03", the wire's as-of shape. */
const BARE_ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Jun 11, 2026. A bare ISO date ("2026-06-11") is read in UTC so it prints its own
 * calendar day in every timezone (FM-3037); a Date keeps local getters. */
export const formatDate = (date) => {
  if (typeof date === 'string' && BARE_ISO_DATE.test(date)) return formatFullDate(date) || '—';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('en-US', { month: 'short' })} ${d.getDate()}, ${d.getFullYear()}`;
};

/** A bare ISO date as a Date at LOCAL midnight, for props typed `instanceOf(Date)`;
 * `new Date('2026-08-21')` is UTC midnight and reads as Aug 20 in US timezones. */
export const localDate = (value) => {
  if (value instanceof Date) return value;
  if (typeof value === 'string' && BARE_ISO_DATE.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(value);
};

/** Yesterday's date, for "As of" pills (computed at call time so it never goes stale). */
export const yesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d;
};

/** Map a signed value to a ChangeChip tone + arrow direction ({tone, dir}). */
export const toneFromValue = (n) => {
  const up = (n ?? 0) >= 0;
  return { tone: up ? 'gain' : 'loss', dir: up ? 'up' : 'down' };
};
/**
 * Relative timestamp for activity feeds / notifications (mirrors the prototype's
 * fmtTime): ≤24h → "N hours ago", <48h → "Yesterday", else a short date ("Jun 9").
 */
export const formatRelativeTime = (hoursAgo) => {
  if (hoursAgo == null || Number.isNaN(Number(hoursAgo))) return '—';
  const h = Number(hoursAgo);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  if (h < 48) return 'Yesterday';
  const d = new Date();
  d.setHours(d.getHours() - h);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** 1.2M / 284.6K / 950 — compact notation for tight chart labels. */
export const formatCompact = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(
    Number(n),
  );
};

// The balance-trend series travels in THOUSANDS of dollars (the contract's unit). Both
// formatters below take that $K value and print it at the scale the figure actually has —
// a $850 balance is "$850", never "$0.9K" (FM-2546). Sign comes from the value.
const kToDollars = (k) => (k == null || !Number.isFinite(Number(k)) ? null : Number(k) * 1000);

/**
 * A $K trend value as money for the hover card / printed bar caps:
 *   0.85 → "$850" · 12.34 → "$12.3K" · 284.6 → "$284.6K" · 1554.4 → "$1.55M".
 * Absent/non-finite → "—".
 */
export const formatKValue = (k) => {
  const d = kToDollars(k);
  if (d == null) return '—';
  const abs = Math.abs(d);
  const sign = d < 0 ? '-' : '';
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${Math.round(abs).toLocaleString('en-US')}`;
};

/**
 * A $K trend value as a y-axis tick label — the design sparkline's `fmtAxis` rule:
 *   0.85 → "$850" · 1.2 → "$1.2k" · 284.6 → "$285k" · 1554.4 → "$1.6M" · 15000 → "$15M".
 * Under $10K the tick keeps one decimal so neighbouring ticks stay distinct — rounded,
 * $1.2k / $1.6k / $2.0k collapsed to "$1k / $2k / $2k". Absent/non-finite → "".
 */
export const formatKAxis = (k) => {
  const d = kToDollars(k);
  if (d == null) return '';
  const abs = Math.abs(d);
  const sign = d < 0 ? '-' : '';
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(abs >= 1e7 ? 0 : 1)}M`;
  if (abs >= 1e4) return `${sign}$${Math.round(abs / 1e3)}k`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}k`;
  return `${sign}$${Math.round(abs).toLocaleString('en-US')}`;
};

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format an ISO date for a balance/value-trend x-axis, by cadence — the granularity widens
 * with the span so labels stay legible (matches the Google/Yahoo-Finance pattern):
 *   weekly → "May 13" · monthly → "May 13 '25" · quarterly → "May 2025" · yearly → "2025".
 * Uses UTC getters so a bare "2021-09-03" doesn't shift a day across timezones. en-US only
 * (financial dates aren't relocalized — LOCALIZATION §16). Empty/invalid → "".
 */
export const formatTrendDate = (iso, cadence) => {
  if (!iso) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth(); // 0-11
  switch (cadence) {
    case 'monthly':
      return `${MONTHS_SHORT[m]} ${d.getUTCDate()} '${String(y).slice(-2)}`;
    case 'quarterly':
      return `${MONTHS_SHORT[m]} ${y}`;
    case 'yearly':
      return `${y}`;
    case 'weekly':
    default:
      return `${MONTHS_SHORT[m]} ${d.getUTCDate()}`;
  }
};

/**
 * Build `count` ascending ISO dates ending at the as-of day, stepping back by cadence
 * (weekly | monthly | quarterly | yearly; default weekly). UTC math for timezone stability.
 * Used to synthesize per-point trend dates from an as-of day when the backend hasn't shipped
 * them yet, and by the mock. Returns undefined when count/asOf is missing.
 */
export const buildTrendDates = (count, cadence, asOfISO) => {
  if (!count || !asOfISO) return undefined;
  const base = new Date(`${asOfISO}T00:00:00Z`);
  if (Number.isNaN(base.getTime())) return undefined;
  const stepBack = (n) => {
    const d = new Date(base.getTime());
    if (cadence === 'monthly') d.setUTCMonth(d.getUTCMonth() - n);
    else if (cadence === 'quarterly') d.setUTCMonth(d.getUTCMonth() - 3 * n);
    else if (cadence === 'yearly') d.setUTCFullYear(d.getUTCFullYear() - n);
    else d.setUTCDate(d.getUTCDate() - 7 * n); // weekly (default)
    return d.toISOString().slice(0, 10);
  };
  return Array.from({ length: count }, (_, i) => stepBack(count - 1 - i));
};

/**
 * Full "MMM D, YYYY" date (e.g. "Jun 3, 2026"). UTC getters so a bare ISO date
 * ("2026-06-03") doesn't shift a day across timezones. en-US only. Empty/invalid → "".
 */
export const formatFullDate = (iso) => {
  if (!iso) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
};

/**
 * "DD MMM,YY" (e.g. "03 Jun,26") — the day-first form the trend charts use in BOTH places a
 * date appears: the x-axis ticks (trendTicks prints this same shape) and the hover card.
 * The card used to answer "Jun 3, 2026" against an axis reading "03-Jun-26" — two orders
 * and two separators for one date, a foot apart on one chart. UTC getters, same reason as
 * `formatFullDate`. Empty/invalid → "".
 */
export const formatDayMonthYear = (iso) => {
  if (!iso) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getUTCDate()).padStart(2, '0');
  const yy = String(d.getUTCFullYear()).slice(-2);
  return `${day} ${MONTHS_SHORT[d.getUTCMonth()]},${yy}`;
};

/**
 * Display date for a value that may be EITHER a bare ISO date string or a Date.
 *
 * The two need different getters and mixing them is an off-by-one-day bug on money
 * dates: `new Date('2026-06-03')` is UTC midnight, so local getters report Jun 2 in
 * every negative-offset timezone. A real Date (e.g. `yesterday()`) is already local
 * wall-clock and must keep local getters, or it would shift the other way.
 *
 * Use this wherever a date can arrive from a payload (`account.asOfDate`) or be
 * constructed in the browser. `utils/pending.js` guards the same trap the same way.
 */
export const formatDisplayDate = (value) => (
  typeof value === 'string' && BARE_ISO_DATE.test(value)
    ? formatFullDate(value)
    : formatDate(value)
);

/**
 * 08/15/2026 — a compact numeric date for dense tables where a written month would
 * wrap the column (the sponsor payroll register). UTC getters for a bare ISO date, for
 * the same off-by-one-day reason `formatDisplayDate` documents.
 *
 * `{ shortYear: true }` cuts the year to two digits (08/15/26) — ~14px narrower per
 * cell, which is what lets the payroll register keep all four of its columns in a
 * ~300px card instead of dropping one. Only for space-starved layouts: a two-digit
 * year is ambiguous, so the full year stays the default everywhere else.
 */
export const formatNumericDate = (value, { shortYear = false } = {}) => {
  if (!value) return '';
  const bare = typeof value === 'string' && BARE_ISO_DATE.test(value);
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const [m, day, y] = bare
    ? [d.getUTCMonth() + 1, d.getUTCDate(), d.getUTCFullYear()]
    : [d.getMonth() + 1, d.getDate(), d.getFullYear()];
  const year = shortYear ? String(y % 100).padStart(2, '0') : y;
  return `${String(m).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
};

/**
 * `ticks` evenly-spaced indices across a series of `count` points — the first and last
 * points are always included, the rest spread uniformly between them (deduped, ascending).
 * The trend chart uses these for its x-axis so the dated ticks are evenly distributed across
 * the width (the Google/Yahoo-Finance pattern) regardless of point count or cadence.
 * count ≤ 1 → the single index (or []); ticks ≤ 1 → just the endpoints.
 */
export const evenTickIndices = (count, ticks = 5) => {
  if (!count || count < 1) return [];
  if (count <= ticks) return Array.from({ length: count }, (_, i) => i);
  const n = Math.max(2, ticks);
  const step = (count - 1) / (n - 1);
  const out = [];
  for (let k = 0; k < n; k += 1) {
    const i = Math.round(k * step);
    if (out[out.length - 1] !== i) out.push(i);
  }
  return out;
};

// Step sizes a reader takes in without effort. Anything else (3s, 7s, 17s) makes them do
// arithmetic to place a line between gridlines.
const NICE_AXIS_STEPS = [1, 2, 2.5, 5, 10];

/**
 * A y-axis top that divides into `intervals` round steps, for a value axis baselined at 0.
 *
 * Recharts only chooses nice tick values for an ['auto','auto'] domain; hand it an exact
 * number and it splits the range evenly, so a 284.6 top yields ticks like 71.15. Rounding
 * the top up to its leading digit isn't enough either — 42.85 → 50 still divides into 16.7s.
 * So the STEP is chosen first, from the list above, and the top follows from it.
 *
 *   niceAxisTop(284.6, 3) → 300   (0 / 100 / 200 / 300)
 *   niceAxisTop(42.85, 3) → 60    (0 / 20 / 40 / 60)
 *   niceAxisTop(8.42, 3)  → 15    (0 / 5 / 10 / 15)
 *
 * Returns undefined for a non-positive or non-finite max, so a caller can fall back to
 * letting the chart library size the axis itself.
 */
export const niceAxisTop = (max, intervals = 3) => {
  if (!(max > 0) || !Number.isFinite(max) || !(intervals >= 1)) return undefined;
  const rough = max / intervals;
  const mag = 10 ** Math.floor(Math.log10(rough));
  const step = (NICE_AXIS_STEPS.find((s) => s * mag >= rough) ?? 10) * mag;
  return step * intervals;
};

/**
 * The tick values for a 0-based value axis, choosing the gridline COUNT as well as the step.
 *
 * `niceAxisTop` takes the interval count as given, which can leave a lot of empty plot:
 * 284.6 over 4 intervals rounds to a 400 top, so the series only reaches 71% of the height.
 * Trying a few counts and keeping the tightest top fixes that (284.6 → 300 over 3), at the
 * cost of a varying number of gridlines — which readers don't notice, unlike dead space.
 *
 *   niceAxisTicks(284.6) → [0, 100, 200, 300]
 *   niceAxisTicks(29.1)  → [0, 10, 20, 30]
 *
 * Returns [] when there's no positive max, so a caller can let the chart size its own axis.
 */
export const niceAxisTicks = (max, counts = [4, 3, 5]) => {
  const best = counts
    .map((n) => ({ n, top: niceAxisTop(max, n) }))
    .filter((c) => c.top != null)
    .sort((a, b) => a.top - b.top || a.n - b.n)[0];
  if (!best) return [];
  return Array.from({ length: best.n + 1 }, (_, k) => (best.top / best.n) * k);
};

/**
 * "Nice" round ticks spanning a RANGE (not from zero) — the design's sparkline `niceTicks`.
 *
 * The balance-trend axis is the case: a 13-year balance moving between $1.30M and $1.55M
 * over the year wants ticks like [1.3M, 1.4M, 1.5M, 1.6M], not a zero baseline that
 * flattens the whole series into the top eighth of the plot. Returns EXACTLY `target`
 * ticks whenever a round step (1/1.5/2/2.5/3/4/5/6/8 × magnitude) can span the range in
 * `target - 1` intervals; only when none can does it fall back to a 1/2/5 ladder yielding
 * between 3 and `target` ticks.
 *
 *   niceRangeTicks(1304, 1554, 4) → [1300, 1400, 1500, 1600]
 *   niceRangeTicks(71, 84, 4)     → [70, 75, 80, 85]
 *
 * Returns [] for a non-finite or empty range so the chart can fall back to hiding its axis.
 */
export const niceRangeTicks = (minIn, maxIn, target = 4) => {
  if (!Number.isFinite(minIn) || !Number.isFinite(maxIn)) return [];
  const min = Math.min(minIn, maxIn);
  const max = Math.max(minIn, maxIn) <= min ? min + 1 : Math.max(minIn, maxIn);
  const range = max - min;
  // EXACTLY `target` ticks first (the hero axis is fixed at four points — decision
  // 2026-08-28): a step from a widened ladder (1/1.5/2/2.5/3/4/5/6/8) sized so
  // `target - 1` intervals from the floored low cover the high. Only when no such
  // step exists does the 3..target search below decide the count.
  const rough = range / (target - 1);
  const exactMag = 10 ** Math.floor(Math.log10(rough));
  for (const base of [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) {
    const step = base * exactMag;
    const lo = Math.floor(min / step) * step;
    if (lo + step * (target - 1) >= max - step * 1e-9) {
      return Array.from({ length: target }, (_, k) => Number((lo + step * k).toPrecision(12)));
    }
  }
  const mag = 10 ** Math.floor(Math.log10(range / 2));
  let best = null;
  [1, 2, 5, 10, 20, 50].forEach((base) => {
    const step = base * mag;
    const lo = Math.floor(min / step) * step;
    const hi = Math.ceil(max / step) * step;
    const count = Math.round((hi - lo) / step) + 1;
    if (count < 3 || count > target) return;
    if (!best || Math.abs(count - target) < Math.abs(best.count - target)
      || (Math.abs(count - target) === Math.abs(best.count - target) && step < best.step)) {
      best = { step, lo, hi, count };
    }
  });
  if (!best) {
    const step = mag * 2;
    const lo = Math.floor(min / step) * step;
    const hi = Math.ceil(max / step) * step;
    best = { step, lo, hi, count: Math.round((hi - lo) / step) + 1 };
  }
  return Array.from({ length: best.count }, (_, k) => Number((best.lo + best.step * k).toPrecision(12)));
};

/**
 * Monday, Jul 14 · 2:47 PM — a past moment, with its weekday, for "last login".
 *
 * The weekday is DERIVED, never carried in the data: a hand-written "Monday" beside a
 * date is one fixture edit away from disagreeing with itself.
 *
 * Parsed as LOCAL time when the value has no zone suffix, which keeps it stable in tests
 * and shows the participant their own clock rather than UTC. en-US by decision — dates are
 * not relocalized here, only the labels around them (LOCALIZATION.md).
 */
export const formatDateTime = (value) => {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} · ${time}`;
};
